import { delay } from 'Utils/asyncFunctions';
import { executeWithTimeout, measureAsset } from './promiseWithRetry.utils';
import { LoadError } from './promiseWithRetry.models';

type LoggerArguments = [string, string | undefined, object | undefined];

type LoggerPattern = {
  warn(...args: LoggerArguments): void;
  error(...args: LoggerArguments): void;
}

let logger: LoggerPattern | undefined;
const MAX_RETRIES = 1;
const SLEEP_INCREMENT = 1000;
const ASSET_TIMEOUT = 21000;

export const setPromiseWithRetryLogger = (loggerService: LoggerPattern) => {
  logger = loggerService;
};

export const promiseWithRetry = async <T>(
  dynamicImport: () => Promise<T>,
  description: string,
  {
    retriesLeft = MAX_RETRIES,
    sleep = SLEEP_INCREMENT,
    timeout = ASSET_TIMEOUT,
  } = {},
): Promise<T> => {
  const trace = `Asset Load.${description}`;

  try {
    const chunk = await executeWithTimeout(dynamicImport, timeout, description);

    return chunk;
  } catch (error) {
    if (retriesLeft < 1) {
      const errorMsg = 'Asset load failed, no retries left';

      logger?.error(errorMsg, trace, { error, retriesLeft, time: measureAsset(description) });

      throw new LoadError(error instanceof Error ? error : errorMsg);
    }
    const waitTime = (MAX_RETRIES - retriesLeft + 1) * sleep;
    const errorMsg = 'Asset load failed, retrying';

    logger?.warn(errorMsg, trace, { error, retriesLeft, time: measureAsset(description) });

    await delay(waitTime);

    return promiseWithRetry(dynamicImport, description, { retriesLeft: retriesLeft - 1, timeout });
  }
};
